/**
 * Analytics hooks for the UBP Q4 calculator.
 *
 * All events are GASv3 `ui` type fired via `useAnalyticsEvents()`.
 * The analytics channel is "marketing" (consistent with other WAC surfaces).
 *
 * Naming convention:
 *   actionSubject  — the element the user interacted with
 *   action         — what happened ("viewed" | "clicked" | "selected")
 *
 * Attribute naming follows camelCase. Common attributes are collected via
 * `useUbpAnalyticsAttributes()` and spread into every event so callers only
 * need to add the event-specific extras.
 */
import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSelector } from './context/hooks';
import {
	selectEstimatedUsageRange,
	selectMeter,
	selectOrgUserCount,
	selectSelectedProducts,
	selectUsage,
	selectUsageType,
} from './context/selectors';

import type { ProductKey } from './calculator/types';
import type { FeedbackVote } from './components/feedback-buttons';
import type { Meter, UsageType } from './context/types';

const ANALYTICS_CHANNEL = 'marketing';

// ─── Shared attribute helpers ──────────────────────────────────────────────

/**
 * Reads the common context attributes that every event should include.
 * Call this at the top of each analytics hook so we don't repeat the
 * selector boilerplate in every hook body.
 */
export const useUbpAnalyticsAttributes = () => {
	const meter = useSelector(selectMeter);
	const orgUserCount = useSelector(selectOrgUserCount);
	const usageType = useSelector(selectUsageType);
	const usage = useSelector(selectUsage);
	const { low, high } = useSelector(selectEstimatedUsageRange);
	const selectedProducts = useSelector(selectSelectedProducts);

	return {
		meterName: meter,
		orgCount: orgUserCount,
		usageProfileName: usageType,
		// Only include customUsageCount when the user entered a non-zero value.
		customUsageCount: usage > 0 ? usage : undefined,
		// Monthly cost estimate represented as a range string (e.g. "145,000–160,000").
		monthlyCostEstimate: `${low.toLocaleString('en-US')}–${high.toLocaleString('en-US')}`,
		productNames: selectedProducts.map((p) => p.productKey),
	};
};

// ─── Step 1 (UsageStep) ────────────────────────────────────────────────────

export const useUbpStep1ScreenViewedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Viewed',
			actionSubject: 'Usage Based Pricing Calculator Step 1',
			eventType: 'screen',
			name: 'ubpCalculatorStep1Screen',
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent]);
};

export const useUbpMeterSelectedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(
		(meterName: Meter) => {
			createAnalyticsEvent({
				action: 'selected',
				actionSubject: 'radioButton',
				eventType: 'ui',
				attributes: {
					...attrs,
					meterName,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, attrs],
	);
};

export const useUbpStep1NextClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Next Button',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

// ─── Step 2 (NeedsStep) ────────────────────────────────────────────────────

export const useUbpStep2ScreenViewedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'Usage Based Pricing Calculator Step 2',
			eventType: 'ui',
			attributes: {
				meterName: attrs.meterName,
				orgCount: attrs.orgCount,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpUsageProfileSelectedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(
		(usageProfileName: UsageType) => {
			createAnalyticsEvent({
				action: 'selected',
				actionSubject: 'radioButton',
				eventType: 'ui',
				attributes: {
					...attrs,
					usageProfileName,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, attrs],
	);
};

export const useUbpStep2GetEstimateClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Get Estimate Button',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep2BackClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Back Button',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

// ─── Step 3 (EstimateStep) ─────────────────────────────────────────────────

export const useUbpStep3ScreenViewedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Viewed',
			actionSubject: 'Usage Based Pricing Calculator Step 3',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep3CompareNowClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Compare Now Button',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep3ExploreServiceCollectionClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'Explore service collection link',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep3BackClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Back Button',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep3ShareClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Share Button',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep3FeedbackClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(
		(vote: 'up' | 'down') => {
			createAnalyticsEvent({
				action: 'Clicked',
				actionSubject: 'Feedback',
				eventType: 'ui',
				attributes: {
					...attrs,
					feedbackVote: vote,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, attrs],
	);
};

// ─── Step 4 (AllowanceStep) ────────────────────────────────────────────────

export const useUbpStep4ScreenViewedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Viewed',
			actionSubject: 'Usage Based Pricing Calculator Step 4',
			eventType: 'ui',
			attributes: {
				meterName: attrs.meterName,
				orgCount: attrs.orgCount,
				usageProfileName: attrs.usageProfileName,
				customUsageCount: attrs.customUsageCount,
				productNames: attrs.productNames,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep4TabClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(
		(productName: string) => {
			createAnalyticsEvent({
				action: 'Clicked',
				actionSubject: 'Tab',
				eventType: 'ui',
				attributes: {
					meterName: attrs.meterName,
					orgCount: attrs.orgCount,
					usageProfileName: attrs.usageProfileName,
					customUsageCount: attrs.customUsageCount,
					productName,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, attrs],
	);
};

export const useUbpStep4SelectButtonClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return useCallback(
		(productName: ProductKey) => {
			createAnalyticsEvent({
				action: 'Clicked',
				actionSubject: 'Select Button',
				eventType: 'ui',
				attributes: {
					productName,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);
};

export const useUbpStep4DeselectButtonClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return useCallback(
		(productName: ProductKey) => {
			createAnalyticsEvent({
				action: 'Unclicked',
				actionSubject: 'Select Button',
				eventType: 'ui',
				attributes: {
					productName,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);
};

export const useUbpStep4CompareClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Compare Button',
			eventType: 'ui',
			attributes: {
				meterName: attrs.meterName,
				orgCount: attrs.orgCount,
				usageProfileName: attrs.usageProfileName,
				customUsageCount: attrs.customUsageCount,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep4BackClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Back Button',
			eventType: 'ui',
			attributes: {
				meterName: attrs.meterName,
				orgCount: attrs.orgCount,
				usageProfileName: attrs.usageProfileName,
				customUsageCount: attrs.customUsageCount,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

// ─── Step 5 (SummaryStep) ─────────────────────────────────────────────────

export const useUbpStep5ScreenViewedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	const selectedProducts = useSelector(selectSelectedProducts);
	// "Usage price = yes or no" — whether any selected product has a
	// usage-based price component. We treat this as whether usage > 0.
	const usage = useSelector(selectUsage);
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Viewed',
			actionSubject: 'Usage Based Pricing Calculator Step 5',
			eventType: 'ui',
			attributes: {
				...attrs,
				productNames: selectedProducts.map((p) => p.productKey),
				hasUsagePrice: usage > 0 ? 'yes' : 'no',
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs, selectedProducts, usage]);
};

export const useUbpStep5BackClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Back Button',
			eventType: 'ui',
			attributes: {
				meterName: attrs.meterName,
				orgCount: attrs.orgCount,
				usageProfileName: attrs.usageProfileName,
				customUsageCount: attrs.customUsageCount,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep5ShareClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(() => {
		createAnalyticsEvent({
			action: 'Clicked',
			actionSubject: 'Share Button',
			eventType: 'ui',
			attributes: attrs,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, attrs]);
};

export const useUbpStep5FeedbackClickedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attrs = useUbpAnalyticsAttributes();
	return useCallback(
		(vote: NonNullable<FeedbackVote>) => {
			createAnalyticsEvent({
				action: 'Clicked',
				actionSubject: 'Feedback',
				eventType: 'ui',
				attributes: {
					...attrs,
					feedbackVote: vote,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, attrs],
	);
};
